import styles from './ProjectHourBillingOverlayRelatedRecords.module.scss';

import { Typography } from '@mui/material';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import {
  BasicTableColumn,
  Table,
  TableStateBagProvider,
} from '@work4all/components';
import { Collapse } from '@work4all/components/lib/components/collapse';
import { Required } from '@work4all/components/lib/input/labeled-input/components/required/Required';

import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { ProjectHoursBillingBy } from '@work4all/models/lib/Enums/ProjectHoursBillingBy.enum';

import { useFormContextPlus } from '../../../../../containers/mask-overlays/form-plus/use-form-context-plus';
import { useProjectHourBillingRelatedRecordsConfig } from '../hooks';

import { ProjectHourBillingOverlayFormValue } from './types';

type Props = {
  data: (Customer | Contract)[];
};

type TableProps = Pick<Props, 'data'> & {
  columns: BasicTableColumn[];
};

export const ProjectHourBillingOverlayRelatedRecords = (props: Props) => {
  const { watch } = useFormContextPlus<ProjectHourBillingOverlayFormValue>();

  const billingBy = watch('settings.billingBy');
  const customerCodes = watch('settings.customerCodes');
  const orderConfirmationCodes = watch('settings.orderConfirmationCodes');

  const showRelatedRecords = useMemo(() => {
    if (
      billingBy === ProjectHoursBillingBy.CUSTOMER &&
      props.data.length === 1 &&
      customerCodes?.[0] === props.data?.[0]?.id
    ) {
      return false;
    }
    if (
      billingBy === ProjectHoursBillingBy.ORDER_CONFIRMATION &&
      props.data.length === 1 &&
      orderConfirmationCodes?.[0] === props.data?.[0]?.id
    ) {
      return false;
    }
    return true;
  }, [billingBy, customerCodes, orderConfirmationCodes, props.data]);

  const config = useProjectHourBillingRelatedRecordsConfig();

  if (billingBy === ProjectHoursBillingBy.PROJECT) {
    return null;
  }

  if (!showRelatedRecords) {
    return null;
  }

  return (
    <Collapse
      title={
        <>
          {config.title}&nbsp;
          <Required />
        </>
      }
      defaultOpen
    >
      <RelatedRecordsTable {...props} columns={config.columns} />
    </Collapse>
  );
};

const RelatedRecordsTable = (props: TableProps) => {
  const [tableInstance, setTableInstance] = useState<TableInstance | null>(
    null
  );

  return (
    <TableStateBagProvider tableInstance={tableInstance}>
      <RelatedRecordsTableInner ref={setTableInstance} {...props} />
    </TableStateBagProvider>
  );
};

export const RelatedRecordsTableInner = React.forwardRef<
  TableInstance,
  TableProps
>(function PartialInvoicesTableInner({ data, columns }: TableProps, ref) {
  const { t } = useTranslation();
  const { watch } = useFormContextPlus<ProjectHourBillingOverlayFormValue>();

  const billingBy = watch('settings.billingBy');

  return (
    <Table
      ref={ref}
      data={data}
      mode="client"
      pending={false}
      columns={columns}
      className={styles.table}
      selectableRows={false}
      resizableColumns
      classes={{
        tableBody: styles.tableBody,
        headerWrapper: clsx(styles.headerWrapper, {
          [styles.margin]: data.length === 0,
        }),
      }}
      initialSortBy={[
        billingBy === ProjectHoursBillingBy.ORDER_CONFIRMATION
          ? { id: 'contractDate', desc: false }
          : { id: 'name', desc: false },
      ]}
      noRowsRenderer={() => {
        return (
          <Typography variant="caption">
            {t('LISTS_PAGES.NO_RESULTS.DEFAULT')}
          </Typography>
        );
      }}
    />
  );
});
